// Forward-compat: turn locally-stored study marks into a question set in ANY
// framework (the "generate later" arm of the round system).
//
// This is a thin dispatcher, NOT a fork of the generation pipeline. It starts a
// normal run of the chosen framework with `include_marking` (so the universal
// marked pipeline includes the marking step), then stashes the marking payload
// keyed by the new run id. The marking renderer auto-advances with that stashed
// payload on mount (see lecture-marking.tsx), so the learner's existing marks
// flow straight into generation without re-marking.
import { startRun } from "@/api/runs";
import { uploadSourcePdf } from "@/api/lecture";
import type { MarkingUserResponse } from "@/api/lecture";
import type { StartInputs } from "@/api/create-types";
import { loadPdf } from "@/create/lecture/lecture-pdf-store";
import { extractPdfText } from "@/create/lecture/extract-pdf-text";

/** sessionStorage key prefix for a pending auto-advance marking payload. */
export const AUTOSTART_PREFIX = "lecture-autostart:";

/** Pop a stashed auto-advance payload for a run (one-shot). */
export function takeAutostartPayload(runId: string): MarkingUserResponse | null {
  const key = AUTOSTART_PREFIX + runId;
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  sessionStorage.removeItem(key);
  try {
    return JSON.parse(raw) as MarkingUserResponse;
  } catch {
    return null;
  }
}

/**
 * Start a run from a study PDF's marks in the chosen framework (default
 * `learn-a-topic`). Returns the new run id; the caller navigates to
 * `/app/create/run/{id}` where the marking step auto-advances with `payload`.
 * Text extraction + the source-PDF upload are best-effort (scanned/visual-only
 * PDFs still generate from their image snips).
 */
export async function generateFromMarks(opts: {
  pdfRef: string;
  payload: MarkingUserResponse;
  slug?: string;
  title?: string;
  onProgress?: (msg: string) => void;
}): Promise<string> {
  let sourceText = "";
  let sourcePdfKey: string | null = null;
  const blob = await loadPdf(opts.pdfRef);
  if (blob) {
    const file = new File([blob], `${opts.title?.trim() || "study"}.pdf`, {
      type: "application/pdf",
    });
    try {
      sourceText = await extractPdfText(file, (m) => opts.onProgress?.(m));
    } catch {
      /* scanned / no extractable text — the image snips still drive generation */
    }
    // Best-effort: store the PDF so generation can ground natively on it.
    try {
      sourcePdfKey = (await uploadSourcePdf(file)).key;
    } catch {
      /* no source PDF stored — extracted text + snips still drive generation */
    }
  }
  const inputs: StartInputs = {
    language: "en",
    source_text: sourceText,
    local_pdf_ref: opts.pdfRef,
    include_marking: true,
    discussion: true,
  };
  if (sourcePdfKey) inputs.source_pdf_key = sourcePdfKey;
  if (opts.title?.trim()) inputs.title = opts.title.trim();
  opts.onProgress?.("Starting generation…");
  const run = await startRun(opts.slug || "learn-a-topic", inputs);
  sessionStorage.setItem(AUTOSTART_PREFIX + run.id, JSON.stringify(opts.payload));
  return run.id;
}

/**
 * Start a run from a SERVER-persisted study document's marks. The source PDF is
 * already stored server-side (its `storage_key` doubles as the run's
 * `source_pdf_key`) and its text was extracted at upload time, so this neither
 * re-uploads nor re-extracts — it just starts the run and stashes the marking
 * payload for the marking step to auto-advance with. Returns the new run id.
 */
export async function generateFromStudyDocument(opts: {
  storageKey?: string;
  sourceText: string;
  payload: MarkingUserResponse;
  slug?: string;
  title?: string;
}): Promise<string> {
  const inputs: StartInputs = {
    language: "en",
    source_text: opts.sourceText,
    include_marking: true,
    discussion: true,
  };
  if (opts.storageKey) inputs.source_pdf_key = opts.storageKey;
  if (opts.title?.trim()) inputs.title = opts.title.trim();
  const run = await startRun(opts.slug || "learn-a-topic", inputs);
  sessionStorage.setItem(AUTOSTART_PREFIX + run.id, JSON.stringify(opts.payload));
  return run.id;
}

/**
 * Start a run from marks made on plain TEXT (no PDF) — e.g. passages a user
 * highlighted in a project's response. The deep marks carry `selected_text`, so
 * generation grounds on `sourceText` + the marks directly; there's no PDF to
 * load or upload. Same auto-advance mechanism as `generateFromMarks`: stash the
 * payload keyed by the new run id, then the caller navigates to the run.
 */
export async function generateFromTextMarks(opts: {
  sourceText: string;
  payload: MarkingUserResponse;
  slug?: string;
  title?: string;
}): Promise<string> {
  const inputs: StartInputs = {
    language: "en",
    source_text: opts.sourceText,
    include_marking: true,
    discussion: true,
  };
  if (opts.title?.trim()) inputs.title = opts.title.trim();
  const run = await startRun(opts.slug || "learn-a-topic", inputs);
  sessionStorage.setItem(AUTOSTART_PREFIX + run.id, JSON.stringify(opts.payload));
  return run.id;
}
